import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  modelosDoWhatsapp as api,
  type CaixaDeWhatsapp,
  type ModeloDoWhatsapp,
  type NovoModelo,
} from '../api/modelosDoWhatsapp'
import { ErroApi } from '../api/client'
import { Aviso } from '../componentes/Aviso'
import { Botao } from '../componentes/Botao'
import { Cartao } from '../componentes/Cartao'
import { Estado } from '../componentes/Estado'
import { useSessao } from '../auth/useSessao'
import './modelos-whatsapp.css'

/**
 * Os modelos de mensagem do WhatsApp (demanda 231).
 *
 * 🔴 **Fora das 24 h, o WhatsApp só aceita modelo aprovado pela Meta** — e
 * criá-lo exigia o Gerenciador do WhatsApp. Esta tela cria, lista e apaga, caixa
 * a caixa, na conta da Meta de cada uma.
 *
 * ⭐ A recusa vem com o motivo — do nosso servidor, antes de gastar uma ida à
 * Meta, ou da própria Meta. É o motivo que diz o que corrigir.
 */
export function ModelosDeWhatsapp() {
  const { t } = useTranslation()
  const { pode } = useSessao()
  const gerencia = pode('ParametroGerenciar')

  const [caixas, setCaixas] = useState<CaixaDeWhatsapp[] | null>(null)
  const [caixa, setCaixa] = useState<number | null>(null)
  const [modelos, setModelos] = useState<ModeloDoWhatsapp[] | null>(null)
  const [falhou, setFalhou] = useState<string | null>(null)
  const [recado, setRecado] = useState<{ tom: 'sucesso' | 'alerta'; texto: string } | null>(null)
  const [criando, setCriando] = useState(false)
  const [sincronizando, setSincronizando] = useState(false)

  useEffect(() => {
    api
      .caixas()
      .then((lista) => {
        setCaixas(lista)
        if (lista.length > 0) setCaixa(lista[0].id)
      })
      .catch(() => setFalhou(t('modelosWhatsapp.erroCaixas')))
  }, [t])

  const carregar = useCallback(async () => {
    if (caixa === null) return
    setFalhou(null)
    setModelos(null)
    try {
      setModelos(await api.listar(caixa))
    } catch (e) {
      setFalhou(motivo(e) ?? t('modelosWhatsapp.erroModelos'))
    }
  }, [caixa, t])

  useEffect(() => {
    void carregar()
  }, [carregar])

  async function sincronizar() {
    if (caixa === null) return
    setSincronizando(true)
    setRecado(null)
    try {
      await api.sincronizar(caixa)
      setRecado({ tom: 'sucesso', texto: t('modelosWhatsapp.sincronizado') })
    } catch (e) {
      setRecado({ tom: 'alerta', texto: motivo(e) ?? t('comum.erroDescricao') })
    } finally {
      setSincronizando(false)
    }
  }

  async function apagar(modelo: ModeloDoWhatsapp) {
    if (caixa === null) return
    if (!window.confirm(t('modelosWhatsapp.confirmarApagar', { nome: modelo.nome }))) return

    setRecado(null)
    try {
      await api.apagar(caixa, modelo.nome, modelo.id || undefined)
      setRecado({ tom: 'sucesso', texto: t('modelosWhatsapp.apagado', { nome: modelo.nome }) })
      await carregar()
    } catch (e) {
      setRecado({ tom: 'alerta', texto: motivo(e) ?? t('comum.erroDescricao') })
    }
  }

  return (
    <>
      <header className="modelos__topo">
        <div>
          <h1 className="modelos__titulo">{t('modelosWhatsapp.titulo')}</h1>
          <p className="modelos__descricao">{t('modelosWhatsapp.descricao')}</p>
        </div>

        {gerencia && caixa !== null && (
          <div className="modelos__acoes">
            <Botao variante="discreto" onClick={() => void sincronizar()} carregando={sincronizando}>
              {t('modelosWhatsapp.sincronizar')}
            </Botao>
            <Botao variante="primario" onClick={() => setCriando(true)} disabled={criando}>
              {t('modelosWhatsapp.novo')}
            </Botao>
          </div>
        )}
      </header>

      {caixas !== null && caixas.length > 1 && (
        <label className="modelos__caixa">
          <span>{t('modelosWhatsapp.caixa')}</span>
          <select value={caixa ?? ''} onChange={(e) => setCaixa(Number(e.target.value))}>
            {caixas.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nome}
                {c.telefone ? ` · ${c.telefone}` : ''}
              </option>
            ))}
          </select>
        </label>
      )}

      {!gerencia && <Aviso tom="info">{t('modelosWhatsapp.somenteLeitura')}</Aviso>}
      {recado && <Aviso tom={recado.tom}>{recado.texto}</Aviso>}

      {criando && caixa !== null && (
        <FormularioDoModelo
          caixa={caixa}
          aoCancelar={() => setCriando(false)}
          aoCriar={async (nome) => {
            setCriando(false)
            setRecado({ tom: 'sucesso', texto: t('modelosWhatsapp.criado', { nome }) })
            await carregar()
          }}
        />
      )}

      {falhou ? (
        <Estado tipo="erro" titulo={falhou} acao={<Botao onClick={() => void carregar()}>{t('comum.tentarDeNovo')}</Botao>} />
      ) : caixas !== null && caixas.length === 0 ? (
        <Estado tipo="vazio" titulo={t('modelosWhatsapp.semCaixa')} />
      ) : modelos === null ? (
        <Estado tipo="carregando" />
      ) : modelos.length === 0 ? (
        <Estado tipo="vazio" titulo={t('modelosWhatsapp.vazio')} />
      ) : (
        <ul className="modelos__lista">
          {modelos.map((m) => (
            <li key={m.id || m.nome}>
              <Cartao>
                <div className="modelos__cabecalho">
                  <h2 className="modelos__nome">
                    {m.nome}
                    <span className={`modelos__situacao modelos__situacao--${m.situacao.toLowerCase()}`}>
                      {t(`modelosWhatsapp.situacao.${m.situacao}`, m.situacao)}
                    </span>
                  </h2>

                  {gerencia && (
                    <Botao variante="discreto" onClick={() => void apagar(m)}>
                      {t('modelosWhatsapp.apagar')}
                    </Botao>
                  )}
                </div>

                <p className="modelos__meta">
                  {t(`modelosWhatsapp.categoria.${m.categoria}`, m.categoria)} · {m.idioma}
                </p>

                {m.motivoDaRecusa && (
                  <Aviso tom="alerta">{t('modelosWhatsapp.motivoDaRecusa', { motivo: m.motivoDaRecusa })}</Aviso>
                )}

                <Previa cabecalho={m.cabecalho} corpo={m.corpo ?? ''} rodape={m.rodape} botoes={m.botoes} />
              </Cartao>
            </li>
          ))}
        </ul>
      )}
    </>
  )
}

/** A mensagem como o cliente a veria — variáveis já trocadas pelos exemplos. */
function Previa({
  cabecalho,
  corpo,
  rodape,
  botoes,
}: {
  cabecalho?: string | null
  corpo: string
  rodape?: string | null
  botoes?: string[]
}) {
  return (
    <div className="modelos__previa">
      {cabecalho && <strong className="modelos__previa-cabecalho">{cabecalho}</strong>}
      <p className="modelos__previa-corpo">{corpo}</p>
      {rodape && <small className="modelos__previa-rodape">{rodape}</small>}
      {botoes && botoes.length > 0 && (
        <div className="modelos__previa-botoes">
          {botoes.map((b) => (
            <span key={b} className="modelos__previa-botao">
              {b}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

function FormularioDoModelo({
  caixa,
  aoCancelar,
  aoCriar,
}: {
  caixa: number
  aoCancelar: () => void
  aoCriar: (nome: string) => Promise<void>
}) {
  const { t } = useTranslation()

  const [nome, setNome] = useState('')
  const [categoria, setCategoria] = useState<NovoModelo['categoria']>('UTILITY')
  const [idioma, setIdioma] = useState('pt_BR')
  const [corpo, setCorpo] = useState('')
  const [exemplos, setExemplos] = useState<string[]>([])
  const [cabecalho, setCabecalho] = useState('')
  const [rodape, setRodape] = useState('')
  const [botoes, setBotoes] = useState<string[]>(['', '', ''])
  const [erro, setErro] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)

  // Quantas variáveis {{n}} o corpo tem — um campo de exemplo para cada.
  const variaveis = useMemo(() => {
    const numeros = [...corpo.matchAll(/\{\{\s*(\d+)\s*\}\}/g)].map((m) => Number(m[1]))
    return numeros.length === 0 ? 0 : Math.max(...numeros)
  }, [corpo])

  const previa = useMemo(
    () => corpo.replace(/\{\{\s*(\d+)\s*\}\}/g, (_, n: string) => exemplos[Number(n) - 1] || `{{${n}}}`),
    [corpo, exemplos],
  )

  async function enviar() {
    setErro(null)
    setEnviando(true)
    try {
      await api.criar(caixa, {
        nome: nome.trim(),
        categoria,
        idioma: idioma.trim(),
        corpo,
        exemplos: exemplos.slice(0, variaveis),
        cabecalho: cabecalho.trim() || undefined,
        rodape: rodape.trim() || undefined,
        botoes: botoes.map((b) => b.trim()).filter(Boolean),
      })
      await aoCriar(nome.trim())
    } catch (e) {
      setErro(motivo(e) ?? t('comum.erroDescricao'))
    } finally {
      setEnviando(false)
    }
  }

  return (
    <Cartao>
      <h2 className="modelos__form-titulo">{t('modelosWhatsapp.novo')}</h2>

      <div className="modelos__form">
        <label className="modelos__campo">
          <span>{t('modelosWhatsapp.campo.nome')}</span>
          <input value={nome} onChange={(e) => setNome(e.target.value)} placeholder="aviso_de_nota_fiscal" />
          <small>{t('modelosWhatsapp.ajuda.nome')}</small>
        </label>

        <label className="modelos__campo">
          <span>{t('modelosWhatsapp.campo.categoria')}</span>
          <select value={categoria} onChange={(e) => setCategoria(e.target.value as NovoModelo['categoria'])}>
            <option value="UTILITY">{t('modelosWhatsapp.categoria.UTILITY')}</option>
            <option value="MARKETING">{t('modelosWhatsapp.categoria.MARKETING')}</option>
          </select>
          <small>{t('modelosWhatsapp.ajuda.categoria')}</small>
        </label>

        <label className="modelos__campo">
          <span>{t('modelosWhatsapp.campo.idioma')}</span>
          <input value={idioma} onChange={(e) => setIdioma(e.target.value)} />
        </label>

        <label className="modelos__campo">
          <span>{t('modelosWhatsapp.campo.cabecalho')}</span>
          <input value={cabecalho} maxLength={60} onChange={(e) => setCabecalho(e.target.value)} />
        </label>

        <label className="modelos__campo modelos__campo--largo">
          <span>{t('modelosWhatsapp.campo.corpo')}</span>
          <textarea rows={4} maxLength={1024} value={corpo} onChange={(e) => setCorpo(e.target.value)} />
          <small>{t('modelosWhatsapp.ajuda.corpo')}</small>
        </label>

        {Array.from({ length: variaveis }, (_, i) => (
          <label key={i} className="modelos__campo">
            <span>{t('modelosWhatsapp.campo.exemplo', { n: i + 1 })}</span>
            <input
              value={exemplos[i] ?? ''}
              onChange={(e) => {
                const proximos = [...exemplos]
                proximos[i] = e.target.value
                setExemplos(proximos)
              }}
            />
          </label>
        ))}

        <label className="modelos__campo">
          <span>{t('modelosWhatsapp.campo.rodape')}</span>
          <input value={rodape} maxLength={60} onChange={(e) => setRodape(e.target.value)} />
        </label>

        {botoes.map((b, i) => (
          <label key={i} className="modelos__campo">
            <span>{t('modelosWhatsapp.campo.botao', { n: i + 1 })}</span>
            <input
              value={b}
              maxLength={25}
              onChange={(e) => {
                const proximos = [...botoes]
                proximos[i] = e.target.value
                setBotoes(proximos)
              }}
            />
          </label>
        ))}
      </div>

      <p className="modelos__rotulo-previa">{t('modelosWhatsapp.previa')}</p>
      <Previa
        cabecalho={cabecalho}
        corpo={previa}
        rodape={rodape}
        botoes={botoes.map((x) => x.trim()).filter(Boolean)}
      />

      {erro && <Aviso tom="alerta">{erro}</Aviso>}

      <div className="modelos__acoes">
        <Botao variante="primario" onClick={() => void enviar()} carregando={enviando}>
          {t('modelosWhatsapp.enviar')}
        </Botao>
        <Botao variante="discreto" onClick={aoCancelar}>
          {t('comum.cancelar')}
        </Botao>
      </div>
    </Cartao>
  )
}

/** O motivo que o servidor (ou a Meta, por ele) deu — é o que diz o que corrigir. */
function motivo(e: unknown): string | null {
  const corpo = e instanceof ErroApi ? (e.corpo as { detail?: string } | null) : null
  return corpo?.detail ?? null
}
